<template>
  <v-row justify="center">
    <WithCustomerOrder v-slot="{ order, loading, error }">
      <v-col cols="12" md="8" lg="6">
        <BtnBaseBlue
          exact
          outlined
          :block="$mq | mq({ xs: true, md: false })"
          :to="{ name: CustomerRoutes.ORDERS_INDEX }"
          ><v-icon small left>mdi-arrow-left</v-icon>Back to My
          Orders</BtnBaseBlue
        >
        <v-card
          :class="$mq | mq({ xs: 'mt-4 pa-0', md: 'mt-4 pa-5' })"
          :outlined="$mq | mq({ xs: false, md: true })"
          :flat="$mq | mq({ xs: true, md: false })"
        >
          <transition name="fade" mode="out-in" appear>
            <InvoiceMissingID
              v-if="!$route.params.orderId"
              key="invoice-missing"
            />
            <div v-else-if="loading" class="text-center" key="loading-invoice">
              <h3 class="mt-2">Loading Order</h3>
              <v-progress-circular
                indeterminate
                class="mt-2"
                size="20"
                width="2"
              />
            </div>
            <v-row v-else-if="error" class="text-sm-center">
              <v-col cols="12" sm="8" class="mx-auto">
                <h2 v-html="error" key="invoice-error" />
                <BtnBaseBlue @click="reloadPage" class="my-6" depressed large
                  >Reload this Page</BtnBaseBlue
                >
                <h3
                  >If you continue to see this error, please visit your
                  <BtnBaseBlue
                    :to="{ name: CustomerRoutes.ORDERS_INDEX }"
                    class="pa-0"
                    text
                    exact
                  >
                    Order History
                  </BtnBaseBlue>
                  and try to locate your order there.</h3
                >
              </v-col>
            </v-row>
            <div v-else key="invoice">
              <v-row
                dense
                class="px-4 proxima--font grey--text text--darken-2"
                no-gutters
              >
                <v-col cols="12" sm="6">Order #{{ ticketNumber }}</v-col>
                <v-col
                  v-if="order.time_ordered_for_invoice"
                  cols="12"
                  sm="6"
                  :class="$mq | mq({ xs: 'text-left', md: 'text-right' })"
                  >Ordered {{ order.time_ordered_for_invoice }}</v-col
                >
              </v-row>
              <v-card-title v-if="!order.completed">
                <h1 class="font-italic">{{
                  getShopDetail('Guest Invoice Title', 'See ya soon.')
                }}</h1>
              </v-card-title>
              <v-card-text>
                <div v-if="order.completed" class="d-block">
                  <h2 class="text-h4 font-weight-bold black--text font-italic"
                    >Order Completed</h2
                  >
                  <h4 class="text-h6 mb-2"
                    >This order has been completed. Enjoy!</h4
                  >
                  <div class="text-h6"
                    >Scheduled for:
                    <span class="font-weight-bold"
                      >{{ order.fulfillment_slot }} |
                      {{ order.availability_name }}</span
                    ></div
                  >
                </div>
                <div v-else class="d-block">
                  <div class="text-h6"
                    >We have received your pre-order and it will be ready
                    for:</div
                  >
                  <div class="text-h6 font-weight-bold"
                    >{{ order.fulfillment_slot }} |
                    {{ order.availability_name }}</div
                  >
                </div>
                <div
                  class="text-h6 mt-3 d-block black--text"
                  v-if="order.is_gift"
                >
                  <v-icon class="mb-1" left small>mdi-gift-outline</v-icon
                  ><span class="font-weight-bold">
                    This Order is a Gift!
                  </span>
                  <div>See below for gift recipient details.</div>
                </div>

                <div class="py-4">
                  <h2
                    v-if="order.paid"
                    class="text-h5 font-weight-bold green--text text--darken-1"
                    >Payment Received<v-icon
                      right
                      color="green darken-1"
                      class="mb-2"
                      >mdi-check-outline</v-icon
                    ></h2
                  >
                  <template v-else-if="!order.paid">
                    <h2
                      class="text-h4 font-weight-bold unpaid red--text text--darken-1 mt-2"
                      >Order Status: Unpaid</h2
                    >
                    <h4 class="text-h5 my-2" v-if="getRequirePrepay"
                      >You must pay for this order to ensure it's reserved.</h4
                    >
                    <v-btn
                      class="white--text my-2"
                      depressed
                      small
                      color="deep-orange darken-1"
                      @click="showDialogInvoicePayment = true"
                      >Pay Now<v-icon right small
                        >mdi-currency-usd</v-icon
                      ></v-btn
                    >
                  </template>
                </div>
                <div class="text-h5"
                  >Items Ordered :
                  <span class="font-weight-bold">{{
                    order.line_items.length
                  }}</span></div
                >
                <div class="text-h5"
                  >Order Total :
                  <span class="font-weight-bold">{{
                    order.grand_total_formatted
                  }}</span></div
                >

                <InvoiceLineItems :items="order.products" />

                <v-divider class="my-4" />
                <v-row dense no-gutters>
                  <v-col class="text-sm-right mr-2">
                    <h3 class="font-weight-black">Subtotal: </h3></v-col
                  >
                  <v-col class="text-right" cols="6" sm="2">
                    <h3>{{ order.subtotal_formatted }}</h3></v-col
                  >
                </v-row>

                <OrderMetaItems :order="order" />

                <v-row dense no-gutters>
                  <v-col class="text-sm-right mr-2">
                    <h3 class="font-weight-black">Total: </h3></v-col
                  >
                  <v-col class="text-right" cols="6" sm="2">
                    <h3>{{ order.grand_total_formatted }}</h3></v-col
                  >
                </v-row>

                <InvoiceDetailsSection title="Customer Details">
                  <InvoiceDetail label="Name" :value="getAuthUserFullName" />
                  <InvoiceDetail
                    v-if="order.special_instructions"
                    label="Special Instructions"
                    :value="order.special_instructions"
                  />
                  <InvoiceDetail
                    v-if="!order.is_gift && order.delivery_address"
                    label="Delivery Address"
                    :value="order.delivery_address"
                  />
                  <InvoiceDetail
                    v-if="order.customer_phone"
                    label="Phone Number"
                    :value="order.customer_phone"
                  />
                </InvoiceDetailsSection>
                <InvoiceDetailsSection
                  v-if="order.is_gift"
                  title="Gift Recipient Details"
                >
                  <InvoiceDetail
                    v-for="(val, key) in JSON.parse(order.giftee_details)"
                    :key="key"
                    :label="_startCase(key)"
                    :value="val"
                  />
                  <InvoiceDetail
                    v-if="order.is_gift && order.delivery_address"
                    label="Delivery Address"
                    :value="order.delivery_address"
                  />
                </InvoiceDetailsSection>

                <InvoiceDetailsSection
                  v-if="getShopDetail('Fulfillment Instructions', null)"
                  title="Fulfillment Instructions"
                >
                  <AirtableHtml
                    setting-name="Fulfillment Instructions"
                    tag-class="text-subtitle-1"
                  />
                </InvoiceDetailsSection>
              </v-card-text>
            </div>
          </transition>
        </v-card>

        <InvoiceFooter v-if="!loading && !error" :paid="order.paid" />

        <DialogInvoicePayment v-if="order" :order="order" />
      </v-col>
    </WithCustomerOrder>
    <DialogInvoiceFulfillment />
  </v-row>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { mapFields } from 'vuex-map-fields'

import _get from 'lodash/get'
import _startCase from 'lodash/startCase'
import fullstory from '@/plugins/fullstory'
import roundToFixed from '@/utils/roundToFixed'
import { CustomerRoutes } from '@/constants/RouteNames'
import OrderModel from '@/models/OrderModel'

import WithCustomerOrder from '@/components/DataProviders/WithCustomerOrder'
import BtnBaseBlue from '@/components/BtnBaseBlue'
import InvoiceLineItems from '@/components/InvoiceLineItems'
import InvoiceFooter from '@/components/InvoiceFooter'
import InvoiceDetailsSection from '@/components/InvoiceDetailsSection'
import InvoiceDetail from '@/components/InvoiceDetail'
import OrderMetaItems from '@/components/OrderMetaItems'
import InvoiceMissingID from '@/components/InvoiceMissingID'
import DialogInvoicePayment from '@/components/DialogInvoicePayment'
import DialogInvoiceFulfillment from '@/components/DialogInvoiceFulfillment'
import AirtableHtml from '@/components/AirtableHtml'

export default {
  name: 'InvoiceView',
  props: {
    orderId: {
      type: [String, Number],
      default: null,
    },
  },
  components: {
    BtnBaseBlue,
    InvoiceLineItems,
    InvoiceDetailsSection,
    InvoiceDetail,
    OrderMetaItems,
    InvoiceMissingID,
    DialogInvoicePayment,
    DialogInvoiceFulfillment,
    AirtableHtml,
    WithCustomerOrder,
    InvoiceFooter,
  },
  data: () => {
    return {
      order: {},
      sections: [],
      items: [],
      error: false,
      loadingShop: false,
      CustomerRoutes,
    }
  },
  computed: {
    ...mapGetters('auth', ['getAuthUserFullName', 'getAuthUser']),
    ...mapGetters('shop', [
      'getShopDetail',
      'getOversoldNote',
      'getRequirePrepay',
      'getSquareLocationID',
      'getFullStoryId',
    ]),
    ...mapFields('order', ['showDialogInvoicePayment']),
    ticketNumber() {
      return _get(OrderModel.find(this.orderId), 'ticket_number', '-')
    }, // ticketNumber
  },
  methods: {
    ...mapActions('auth', ['setupFullStory']),
    reloadPage() {
      let href = window.location.href
      window.location.href = href
    }, // reloadPage
    roundToFixed,
    _startCase,
  },
  watch: {
    getAuthUser: {
      async handler(authUser) {
        let fullStoryId = this.getFullStoryId
        if (authUser && !!fullStoryId) {
          await fullstory.init(fullStoryId)
          this.setupFullStory()
        }
      },
      immediate: true,
    },
  },
}
</script>

<style lang="scss" scoped>
.invoice-footer {
  background-color: rgba(#3f5363, 0.4);
  border-color: rgba(#3f5363, 0.4);
  z-index: 4;
}
</style>
